import { Controller } from "@hotwired/stimulus"

// Live workspace slug checker. Wires the workspace settings form with:
//  - a real-time URL preview ("votre URL : beb.tn/<slug>")
//  - debounced availability lookup against /admin/parametres/check-slug
//  - inline OK/error feedback inside <span data-slug-checker-target="status">
//
// Targets:
//   nameInput   — the workspace name field (read-only source for the slug)
//   slugInput   — the slug field (user-editable)
//   preview     — element to render "beb.tn/<slug>"
//   status      — element to render the availability message (FR)
//
// Values:
//   url — the JSON endpoint to GET, e.g. "/admin/parametres/check-slug"
export default class extends Controller {
  static targets = ["nameInput", "slugInput", "preview", "status"]
  static values  = { url: String }

  connect() {
    this.timeout = null
    this.updatePreview()
    this.checkAvailability()
  }

  // Called when the slug input changes — debounces the availability call.
  onSlugInput() {
    this.updatePreview()
    this.scheduleCheck()
  }

  // Called when the name input changes — only updates the preview, since
  // we don't auto-overwrite the slug (admins may have customised it).
  onNameInput() {
    this.updatePreview()
  }

  scheduleCheck() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.checkAvailability(), 300)
  }

  updatePreview() {
    if (!this.hasPreviewTarget) return
    const slug = this.currentSlug()
    // La page du coach vit à beb.tn/<handle> (26/09/2026, AdresseCourte).
    this.previewTarget.textContent = slug ? `beb.tn/${slug}` : "beb.tn/…"
  }

  async checkAvailability() {
    if (!this.hasStatusTarget || !this.hasUrlValue) return
    const slug = this.currentSlug()
    if (!slug) {
      this.renderStatus("", "neutral")
      return
    }
    try {
      const res = await fetch(`${this.urlValue}?slug=${encodeURIComponent(slug)}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) {
        this.renderStatus("indisponible", "error")
        return
      }
      const data = await res.json()
      this.renderStatus(data.reason, data.available ? "ok" : "error")
    } catch (e) {
      this.renderStatus("erreur réseau", "error")
    }
  }

  currentSlug() {
    const raw = (this.hasSlugInputTarget ? this.slugInputTarget.value : "").trim().toLowerCase()
    return raw
  }

  renderStatus(message, kind) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.dataset.state = kind
  }
}
